import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { SendbirdChatParams } from "@sendbird/chat";
import type {
  GroupChannelModule as GroupChannelModuleType,
  SendbirdGroupChat
} from "@sendbird/chat/groupChannel";
import type { OpenChannelModule as OpenChannelModuleType } from "@sendbird/chat/openChannel";

import { useAppSelector } from "app/hooks";
import notificationSound from "assets/audio/chat-notification-sound.mp3";
import userPhotoFallback from "assets/images/user.png";
import {
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useBrowserNotifications } from "features/browser-notifications/browser-notifications.context";
import { isAppBackgrounded } from "features/browser-notifications/browser-notifications.util";
import useDeferredReady from "hooks/use-deferred-ready.hook";
import { useGetMySendBirdDetailsQuery } from "services/users";
import { importWithRetry } from "utils/lazy-with-retry.util";
import { viteEnv } from "utils/vite-env.util";

import {
  chatNotificationAutoCloseMs,
  isMentioned,
  shouldAlertForChatMessage,
  shouldShowChatNotification
} from "./chat-notification.util";
import useUnreadChatTitle from "./use-unread-chat-title.hook";
import { previewText } from "./v2/markdown/preview-text.util";

interface AppWithGlobalChatProps {
  children: React.ReactNode;
}

const GlobalChatContext = createContext({
  unreadCount: 0,
  lastMessage: null,
  hasNewMessages: false,
  refreshUnreadCount: () => {},
  clearNotifications: () => {},
  sdk: null as any,
  isSDKReady: false
});

export const useGlobalChat = () => useContext(GlobalChatContext);

function GlobalMessageEventProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const [customSDK, setCustomSDK] = useState<SendbirdGroupChat | null>(null);
  const [isSDKReady, setIsSDKReady] = useState(false);
  const defaultPushOption = useRef("all");

  useEffect(() => {
    if (!customSDK) return;
    let cancelled = false;
    const refresh = () => {
      void customSDK
        .getPushTriggerOption()
        .then((option) => {
          if (!cancelled) defaultPushOption.current = option;
        })
        .catch(() => {});
    };
    refresh();
    window.addEventListener("focus", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", refresh);
    };
  }, [customSDK]);

  const navigate = useNavigate();
  const location = useLocation();
  const isOnChatPage = location.pathname.includes("/chat");
  const { notify } = useBrowserNotifications();

  const [unreadCount, setUnreadCount] = useState(0);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  // "(2) Billing | Suno": unread chats stay visible in the tab bar after a
  // notification has closed.
  useUnreadChatTitle(unreadCount);

  // This runs app-wide (mounted for every authenticated page, not just
  // /chat) — deferred so it doesn't compete with the current page's own
  // critical requests for the browser's ~6 concurrent HTTP/1.1 connection
  // slots per domain (ENG-1729). chat.component.tsx reads its live `sdk`
  // from this same provider (via useGlobalChat()) rather than establishing
  // its own connection, so the defer is bypassed when already on /chat —
  // otherwise a direct/fresh landing on /chat would wait on a connection
  // that has no reason to be delayed there.
  const deferredReady = useDeferredReady();

  const { data: sendbirdDetails } = useGetMySendBirdDetailsQuery(undefined, {
    skip: (!deferredReady && !isOnChatPage) || !currentUser?.sendbird_user_id
  });

  // Initialize custom SDK
  useEffect(() => {
    const initializeSDK = async () => {
      if (
        !currentUser?.sendbird_user_id ||
        !sendbirdDetails?.access_token ||
        customSDK
      ) {
        return;
      }

      try {
        const [
          { default: SendbirdChat },
          { GroupChannelModule },
          { OpenChannelModule }
        ] = await Promise.all([
          importWithRetry(() => import("@sendbird/chat"), "sendbird-chat"),
          importWithRetry(
            () => import("@sendbird/chat/groupChannel"),
            "sendbird-group-channel"
          ),
          importWithRetry(
            () => import("@sendbird/chat/openChannel"),
            "sendbird-open-channel"
          )
        ]);
        const params: SendbirdChatParams<
          [GroupChannelModuleType, OpenChannelModuleType]
        > = {
          appId: viteEnv.VITE_SENDBIRD_APP_ID,
          modules: [new GroupChannelModule(), new OpenChannelModule()],
          localCacheEnabled: true
        };
        const sdk = await SendbirdChat.init(params);
        await sdk.connect(
          currentUser.sendbird_user_id,
          sendbirdDetails.access_token
        );

        setCustomSDK(sdk);
        setIsSDKReady(true);
      } catch (error) {
        console.error("Failed to initialize custom SDK:", error);
        setIsSDKReady(false);
      }
    };

    initializeSDK();
  }, [currentUser?.sendbird_user_id, sendbirdDetails?.access_token, customSDK]);

  const updateGlobalUnreadCount = useCallback(() => {
    if (!customSDK || !customSDK.groupChannel) return;
    customSDK.groupChannel
      .getTotalUnreadMessageCount()
      .then((count: number) => setUnreadCount(count))
      .catch((_e: any) => {});
  }, [customSDK]);

  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio(notificationSound);
      audio.volume = 0.3;
      void audio.play().catch(() => {});
    } catch {
      /* Desktop notifications still carry the alert if audio is blocked. */
    }
  }, []);

  const showGlobalNotification = useCallback(
    (channel: any, message: any) => {
      if (
        !shouldAlertForChatMessage(
          channel,
          message,
          currentUser.sendbird_user_id || "",
          defaultPushOption.current
        )
      )
        return;
      const sender = message.sender?.nickname || "Someone";
      const channelName = channel.name || "Chat";
      const mentioned = isMentioned(
        message,
        currentUser.sendbird_user_id || ""
      );
      // Chat notifications show whether Suno is in front or in the background,
      // for messages and @mentions alike, and stay on screen for the person's
      // chosen time (0 = until closed). Someone reading this very conversation
      // sees the message arrive, so it does not pop up. One banner per
      // conversation: a burst of messages replaces itself rather than stacking;
      // mentions keep their own.
      if (
        shouldShowChatNotification({
          enabled: currentUser.chat_notifications_enabled,
          channelUrl: channel.url,
          appInFront: !isAppBackgrounded()
        })
      )
        notify({
          type: "chat",
          id: `${channel.url}:${message.messageId}`,
          collapseKey: mentioned ? `${channel.url}:mention` : channel.url,
          title: mentioned
            ? `${sender} mentioned you in ${channelName}`
            : `${sender} — ${channelName}`,
          body:
            previewText(message.message ?? "").slice(0, 200) ||
            "File attachment",
          icon: message.sender?.profileUrl || userPhotoFallback,
          // Keeps Windows/macOS from hiding it after ~5 s; Suno closes it on time.
          requireInteraction: true,
          autoCloseMs: chatNotificationAutoCloseMs(
            currentUser.chat_notification_seconds
          ),
          showInForeground: true,
          silent: !currentUser.chat_sound_notification,
          onClick: () =>
            navigate(`/chat?channel=${encodeURIComponent(channel.url)}`)
        });
      if (currentUser.chat_sound_notification) playNotificationSound();
    },
    [
      currentUser.sendbird_user_id,
      currentUser.chat_sound_notification,
      currentUser.chat_notifications_enabled,
      currentUser.chat_notification_seconds,
      notify,
      navigate,
      playNotificationSound
    ]
  );

  const handleGlobalNewMessage = useCallback(
    (channel: any, message: any) => {
      // Update global state
      updateGlobalUnreadCount();
      setLastMessage({
        id: message.messageId,
        text: message.message || "File attachment",
        sender: message.sender?.nickname || "Unknown",
        channelName: channel.name || "Unknown Channel",
        channelUrl: channel.url,
        timestamp: message.createdAt
      });
      setHasNewMessages(true);

      showGlobalNotification(channel, message);
    },
    [showGlobalNotification, updateGlobalUnreadCount]
  );

  const setupGlobalHandlers = useCallback(async () => {
    if (!customSDK) return;

    const [{ default: GroupChannelHandler }, { default: ConnectionHandler }] =
      await Promise.all([
        importWithRetry(
          () => import("@sendbird/uikit-react/handlers/GroupChannelHandler"),
          "sendbird-group-channel-handler"
        ),
        importWithRetry(
          () => import("@sendbird/uikit-react/handlers/ConnectionHandler"),
          "sendbird-connection-handler"
        )
      ]);

    const channelHandler = new GroupChannelHandler({
      onMessageReceived: (channel: any, message: any) => {
        if (message.sender?.userId !== currentUser.sendbird_user_id) {
          handleGlobalNewMessage(channel, message);
        }
      },

      onChannelChanged: (_channel: any) => {
        updateGlobalUnreadCount();
      },
      onUnreadMemberStatusUpdated: (_channel: any) => {
        updateGlobalUnreadCount();
      }
    });

    const connectionHandler = new ConnectionHandler({
      onReconnectSucceeded: () => {
        updateGlobalUnreadCount();
      },
      onDisconnected: () => {
        setIsSDKReady(false);
      },
      onReconnectStarted: () => {
        // Reconnection attempt in progress
      },
      onReconnectFailed: () => {
        setIsSDKReady(false);
        console.warn("Sendbird reconnection failed");
      }
    });

    const handlerId = `global-chat-handler-${currentPractice?.id}`;
    const connectionHandlerId = `global-connection-handler-${currentPractice?.id}`;

    customSDK.groupChannel.addGroupChannelHandler(handlerId, channelHandler);
    customSDK.addConnectionHandler(connectionHandlerId, connectionHandler);
    updateGlobalUnreadCount();
  }, [
    customSDK,
    currentPractice?.id,
    updateGlobalUnreadCount,
    currentUser.sendbird_user_id,
    handleGlobalNewMessage
  ]);

  const cleanupGlobalHandlers = useCallback(() => {
    if (!customSDK) return;
    const handlerId = `global-chat-handler-${currentPractice?.id}`;
    const connectionHandlerId = `global-connection-handler-${currentPractice?.id}`;
    if (customSDK.groupChannel?.removeGroupChannelHandler) {
      customSDK.groupChannel.removeGroupChannelHandler(handlerId);
    }
    if (customSDK.removeConnectionHandler) {
      customSDK.removeConnectionHandler(connectionHandlerId);
    }
  }, [customSDK, currentPractice?.id]);

  useEffect(() => {
    if (
      !customSDK ||
      !currentUser.sendbird_user_id ||
      !currentPractice?.sendbird_chat_enabled
    ) {
      return;
    }

    if (customSDK.connectionState === "OPEN") {
      setupGlobalHandlers().catch(() => {});
    } else if (customSDK.connectionState === "CLOSED") {
      if (currentUser.sendbird_user_id && sendbirdDetails?.access_token) {
        customSDK
          .connect(currentUser.sendbird_user_id, sendbirdDetails.access_token)
          .then(() => {
            setupGlobalHandlers().catch(() => {});
          })
          .catch((_e: any) => {});
      }
    }

    return () => {
      cleanupGlobalHandlers();
    };
  }, [
    customSDK,
    currentUser.sendbird_user_id,
    currentPractice?.sendbird_chat_enabled,
    setupGlobalHandlers,
    cleanupGlobalHandlers,
    sendbirdDetails?.access_token
  ]);

  const clearNotifications = useCallback(() => {
    setUnreadCount(0);
    setHasNewMessages(false);
    setLastMessage(null);
  }, []);

  // Context value
  const contextValue = {
    unreadCount,
    lastMessage,
    hasNewMessages,
    refreshUnreadCount: updateGlobalUnreadCount,
    clearNotifications,
    sdk: customSDK,
    isSDKReady
  };

  return (
    <GlobalChatContext.Provider value={contextValue}>
      {children}
    </GlobalChatContext.Provider>
  );
}

export function AppWithGlobalChat({
  children
}: AppWithGlobalChatProps): JSX.Element {
  return <GlobalMessageEventProvider>{children}</GlobalMessageEventProvider>;
}
